import { createSlice } from "@reduxjs/toolkit"

/**
 * Phase 2 — reactive mirror of the IndexedDB outbox/draft state (see
 * lib/offlineDb.js). IndexedDB itself isn't reactive, so the sync manager
 * and draft-autosave hook dispatch into this slice whenever they touch
 * IndexedDB, giving the UI (SyncStatusWidget) a live view of:
 *  - whether the browser currently thinks it's online
 *  - the outbox queue (pending/syncing/failed/synced form saves & uploads)
 *  - the most recent local draft-save timestamp per key
 */

const initialState = {
  isOnline: typeof navigator !== "undefined" ? navigator.onLine : true,
  outboxItems: [], // mirrors lib/offlineDb.js outbox store
  resolvedIds: {},
  lastDraftSavedAt: {}, // { [draftKey]: timestamp }
  isSyncing: false,
  syncGeneration: 0, // Invalidates report reads started before a sync run.
  // Set to true when connectivity returns while items are still queued.
  // Drives the highlighted "Sync now" call-to-action so reconnecting never
  // silently syncs — the user has to see and press it, and it's cleared
  // once they do (or once the queue is confirmed empty).
  hasPendingSyncPrompt: false,
  // Set to true right after a manual sync finishes successfully, so the
  // widget can show a brief "All changes synced" confirmation instead of
  // just disappearing with no feedback.
  lastSyncSucceededAt: null,
  syncRun: { total: 0, completed: 0, items: [] },
}

// Upload bytes live in IndexedDB. Redux only needs a serializable summary
// for the queue UI; sync reads the original File directly from IndexedDB.
const summarizeOutboxItem = (item) => {
  if (!item?.file) return item
  const { name, size, type, lastModified } = item.file
  return { ...item, file: { name, size, type, lastModified } }
}

const offlineSlice = createSlice({
  name: "offline",
  initialState,
  reducers: {
    beginSyncRun: {
      prepare: (items) => ({ payload: items.map(summarizeOutboxItem) }),
      reducer: (state, action) => {
        state.syncGeneration += 1
        state.syncRun = { total: action.payload.length, completed: 0, items: action.payload }
      },
    },
    updateSyncOperation: (state, action) => {
      const { id, ...patch } = action.payload
      const item = state.syncRun.items.find((entry) => entry.id === id)
      if (item) Object.assign(item, patch)
      state.syncRun.completed = state.syncRun.items.filter((entry) => entry.status === "synced").length
    },
    rememberResolvedIds: (state, action) => {
      Object.assign(state.resolvedIds, action.payload)
    },
    setOnlineStatus: (state, action) => {
      state.isOnline = Boolean(action.payload)
    },
    setOutboxItems: {
      prepare: (items) => ({ payload: (items || []).map(summarizeOutboxItem) }),
      reducer: (state, action) => { state.outboxItems = action.payload },
    },
    upsertOutboxItem: {
      prepare: (item) => ({ payload: summarizeOutboxItem(item) }),
      reducer: (state, action) => {
      const item = action.payload
      const index = state.outboxItems.findIndex((i) => i.id === item.id)
      if (index >= 0) {
        state.outboxItems[index] = item
      } else {
        state.outboxItems.push(item)
      }
      },
    },
    removeOutboxItem: (state, action) => {
      state.outboxItems = state.outboxItems.filter((i) => i.id !== action.payload)
    },
    discardSyncOperation: (state, action) => {
      state.outboxItems = state.outboxItems.filter((item) => item.id !== action.payload)
      state.syncRun.items = state.syncRun.items.filter((item) => item.id !== action.payload)
      state.syncRun.total = state.syncRun.items.length
      state.syncRun.completed = state.syncRun.items.filter((item) => item.status === "synced").length
      state.hasPendingSyncPrompt = state.outboxItems.length > 0
    },
    setIsSyncing: (state, action) => {
      state.isSyncing = Boolean(action.payload)
    },
    setHasPendingSyncPrompt: (state, action) => {
      state.hasPendingSyncPrompt = Boolean(action.payload)
    },
    setLastSyncSucceededAt: (state, action) => {
      state.lastSyncSucceededAt = action.payload
    },
    setDraftSavedAt: (state, action) => {
      const { key, timestamp } = action.payload
      state.lastDraftSavedAt[key] = timestamp
    },
    clearSyncedOutboxItems: (state) => {
      state.outboxItems = state.outboxItems.filter((i) => i.status !== "synced")
    },
  },
})

export const {
  beginSyncRun,
  updateSyncOperation,
  rememberResolvedIds,
  setOnlineStatus,
  setOutboxItems,
  upsertOutboxItem,
  removeOutboxItem,
  discardSyncOperation,
  setIsSyncing,
  setHasPendingSyncPrompt,
  setLastSyncSucceededAt,
  setDraftSavedAt,
  clearSyncedOutboxItems,
} = offlineSlice.actions

export default offlineSlice.reducer
